/**
 * Puschies: Zusatzzeilen (Blume/Express) passend zu Perso-Hauptartikeln halten – anlegen, Menge angleichen,
 * verwaiste entfernen. Läuft nach jeder Warenkorb-Änderung (shopify:cart:lines-update) und beim Laden.
 * Muster: assets/p-cart-gifts.js. Logik: assets/p-konfigurator-logic.js (planAddonSync).
 */
import { planAddonSync } from '@theme/p-konfigurator-logic'; // Importmap (versionierte URL, sonst liefert das CDN alte Stände)

const CART_LINES_UPDATE = 'shopify:cart:lines-update';
const configEl = document.getElementById('p-cart-addons-config');
const config = configEl ? JSON.parse(configEl.textContent) : null;

if (config && !window.__puschiesAddonSync) {
  window.__puschiesAddonSync = true;
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
  /** @type {Set<string>} Zusatzzeilen (Variante|Hauptartikel-Key|Feld), deren Hinzufügen fehlgeschlagen ist (z. B.
   *  Variante ausverkauft/gelöscht) → nicht endlos wiederholen. Schlüssel statt reiner Varianten-ID, da dieselbe
   *  Variante (z. B. Blume) für mehrere Hauptartikel/Felder gebraucht wird. */
  const failed = new Set();
  let busy = false;
  let pending = false;
  let timer;

  /** @param {{variantId: number|string, parentKey: string, properties: Record<string,string>}} addition */
  const additionKey = (addition) => `${addition.variantId}|${addition.parentKey}|${addition.properties._zusatz}`;

  /** Theme-Ereignisse (Importmap @shopify/events); fehlen sie, bleibt nur das Neuladen als Rückfall. */
  const eventsModule = import('@shopify/events').catch((error) => {
    console.warn('[p-cart-addons] @shopify/events nicht verfügbar, Rückfall Neuladen:', error);
    return null;
  });

  /** Section-IDs aller Warenkorb-Bereiche (Drawer, Warenkorbseite) → Ajax-Antwort liefert deren HTML gleich mit. */
  const cartSectionIds = () =>
    [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');

  /** @param {string} path @param {object} body */
  const post = (path, body) =>
    fetch(`${root}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json());

  const readCart = () => fetch(`${root}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());

  /** @param {{variantId: number, quantity: number, properties: Record<string,string>, parentKey: string}} a */
  const toItem = (a) => ({ id: Number(a.variantId), quantity: a.quantity, properties: a.properties, parent_line_key: a.parentKey });

  /**
   * Führt Änderungen + Neuanlagen per Ajax aus und meldet sie Theme wie product-form.js: ein
   * CartLinesUpdateEvent (shopify:cart:lines-update) mit Promise, aufgelöst mit dem neuen Warenkorb + Section-HTML.
   * Darauf rendern cart-items-component (Drawer/Warenkorbseite, „externe“ Änderung) und cart-icon (Zähler) neu.
   * @param {{id: string, quantity: number}[]} changes
   * @param {{variantId: number, quantity: number, properties: Record<string,string>, parentKey: string}[]} additions
   */
  const apply = async (changes, additions) => {
    const events = await eventsModule;
    const Event = events?.CartLinesUpdateEvent;
    const deferred = Event?.createPromise();
    if (Event && deferred) {
      document.dispatchEvent(new Event({
        action: 'update', // nicht 'add' → Drawer öffnet nicht von selbst
        context: 'cart',
        lines: changes.map((c) => ({ id: c.id, quantity: c.quantity })),
        promise: deferred.promise,
      }));
    }
    const sections = cartSectionIds();
    const sections_url = window.location.pathname;
    /** @type {Record<string, string> | undefined} */
    let sectionsHtml;
    // cart/change.js liefert (anders als cart/add.js mit `items:[…]`, das nur die neu angelegten Zeilen zurückgibt)
    // den vollständigen Warenkorb zurück - als Rückfall geeignet, falls das abschließende /cart.js scheitert.
    /** @type {{items: unknown[]}|undefined} */
    let lastChangeCart;
    const keep = (response) => { if (response?.sections) sectionsHtml = response.sections; return response; };
    try {
      for (const change of changes) {
        const result = keep(await post('cart/change.js', { id: change.id, quantity: change.quantity, sections, sections_url }));
        if (result && Array.isArray(result.items)) lastChangeCart = result;
      }
      let rest = additions.filter((a) => a.quantity > 0 && !failed.has(additionKey(a)));
      // Normalfall: alle Unterpositionen in EINER Anfrage.
      if (rest.length > 1) {
        const result = keep(await post('cart/add.js', { items: rest.map(toItem), sections, sections_url }));
        if (!result?.status) rest = [];
        else {
          // Die Antwort sagt nicht, WELCHE Zeile scheiterte → Warenkorb neu lesen und den Rest einzeln anlegen,
          // damit nur die tatsächlich fehlgeschlagene Zeile gesperrt wird.
          console.warn('[p-cart-addons] Sammel-Anlage fehlgeschlagen, lege einzeln an:', result.description || result.message);
          rest = planAddonSync((await readCart()).items || [], config).additions
            .filter((a) => a.quantity > 0 && !failed.has(additionKey(a)));
        }
      }
      for (const addition of rest) {
        const key = additionKey(addition);
        const result = keep(await post('cart/add.js', { items: [toItem(addition)], sections, sections_url }).catch(() => ({ status: 0 })));
        if (result?.status !== undefined) {
          if (!failed.has(key)) console.warn('[p-cart-addons] Zusatzzeile konnte nicht angelegt werden, wird nicht erneut versucht:', key);
          failed.add(key);
        }
      }
    } finally {
      // Abschließender Warenkorb-Stand für das CartLinesUpdateEvent: bevorzugt eine frische /cart.js-Lesung;
      // schlägt die fehl (z. B. Netzwerkfehler), bleibt der Drawer sonst auf altem Stand stehen, obwohl
      // Anlegen/Ändern schon durch ist → stattdessen den vollen Warenkorb aus der letzten cart/change.js-Antwort
      // dieses Laufs nehmen (die liefert - anders als cart/add.js - den kompletten Warenkorb mit). Fehlt auch der
      // (z. B. weil es nur Neuanlagen ohne Mengenänderung gab, oder schon die erste Anfrage scheiterte), bleibt
      // `window.location.reload()` die Notlösung.
      const cart = await readCart().catch(() => null) || lastChangeCart || null;
      if (Event && deferred && cart) {
        deferred.resolve({
          cart: Event.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'p-cart-addons', sections: sectionsHtml, didError: false },
        });
      } else if (Event && deferred) {
        // /cart.js fehlgeschlagen UND kein Warenkorb aus add.js/change.js dieses Laufs (z. B. weil schon die
        // erste Anfrage scheiterte) → nur ablehnen ließe den Drawer auf altem Stand stehen, Notlösung: neu laden.
        deferred.reject(new Error('cart refresh failed'));
        window.location.reload();
      } else {
        // @shopify/events gar nicht verfügbar → wie bisher Notlösung: neu laden.
        window.location.reload();
      }
    }
  };

  const sync = async () => {
    if (busy) { pending = true; return; }
    busy = true;
    try {
      const cart = await readCart();
      const { changes, additions } = planAddonSync(cart.items || [], config);
      // Änderungen (Menge angleichen/entfernen) laufen immer – auch für zuvor fehlgeschlagene Zeilen, die es
      // inzwischen doch in den Warenkorb geschafft haben (z. B. manuell durch Support).
      const open = additions.filter((a) => a.quantity > 0 && !failed.has(additionKey(a)));
      if (changes.length || open.length) await apply(changes, open);
    } catch (error) {
      console.error('[p-cart-addons]', error);
    } finally {
      busy = false;
      // `pending` fängt Ereignisse ab, die während dieses Laufs eintrafen (z. B. unser eigenes CartLinesUpdateEvent
      // löst shopify:cart:lines-update erneut aus) – der Folgelauf findet dann nichts mehr zu tun und endet, statt
      // erneut zu planen. Zusammen mit dem `failed`-Set (oben) bleibt das endlich, auch wenn eine Zusatzzeile
      // dauerhaft nicht angelegt werden kann.
      if (pending) { pending = false; await sync(); }
      settle();
    }
  };

  /* ---------- Ruhezustand: kein Timer, kein Lauf, keine offene Warenkorb-Anfrage ---------- */
  let openRequests = 0;
  /** @type {Array<() => void>} */
  let idleWaiters = [];
  // Theme entprellt Mengenänderungen im Warenkorb (component-cart-items.js, 300 ms) – zwischen Klick und
  // shopify:cart:lines-update liegt also eine Lücke. Solange eine Mengenänderung angekündigt ist, gilt der
  // Warenkorb als „in Bewegung“ (Sicherheitsnetz 1,5 s, falls kein Ereignis folgt).
  let quantityPending = false;
  let quantityTimer;
  const isIdle = () => !busy && !pending && !timer && openRequests === 0 && !quantityPending;
  const settle = () => {
    if (!isIdle()) return;
    const waiters = idleWaiters;
    idleWaiters = [];
    waiters.forEach((resolve) => resolve());
  };
  const whenIdle = () => (isIdle() ? Promise.resolve() : new Promise((resolve) => idleWaiters.push(resolve)));

  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(() => { timer = undefined; sync(); }, 150);
  };

  // Theme feuert shopify:cart:lines-update VOR der eigentlichen Anfrage (product-form.js, component-cart-items.js)
  // und hängt deren Promise an → erst danach /cart.js lesen (wie p-cart-gifts.js).
  document.addEventListener('quantity-selector:update', () => {
    quantityPending = true;
    clearTimeout(quantityTimer);
    quantityTimer = setTimeout(() => { quantityPending = false; settle(); }, 1500);
  });
  document.addEventListener(CART_LINES_UPDATE, (event) => {
    quantityPending = false;
    clearTimeout(quantityTimer);
    const promise = /** @type {any} */ (event).promise;
    if (promise?.then) {
      openRequests += 1;
      const done = () => { openRequests -= 1; schedule(); };
      promise.then(done, done);
    } else {
      schedule();
    }
  });
  schedule();

  /* ---------- Kasse: vor dem Weiterleiten einen vollständigen Abgleich abwarten ----------
     Fängt das Absenden des Warenkorb-Formulars mit dem Checkout-Button (Drawer + Warenkorbseite) ab, wartet bis
     alle offenen Änderungen durch sind, gleicht einmal frisch ab und sendet dann erneut ab. Scheitert der Abgleich
     oder dauert er zu lange, geht es trotzdem zur Kasse (nie blockieren). */
  const CHECKOUT_TIMEOUT = 6000;
  // Andere Warenkorb-Skripte (z. B. Globo gpomain.js) laden die Seite nach eigenen Änderungen neu und würden den
  // wartenden Kassen-Klick verwerfen → Absicht kurz merken und nach dem Neuladen fortsetzen.
  const CHECKOUT_INTENT = 'p-cart-addons:checkout';
  const CHECKOUT_INTENT_MAX_AGE = 15000;
  const storage = {
    set() { try { sessionStorage.setItem(CHECKOUT_INTENT, String(Date.now())); } catch { /* privat/gesperrt */ } },
    take() {
      try {
        const at = Number(sessionStorage.getItem(CHECKOUT_INTENT));
        sessionStorage.removeItem(CHECKOUT_INTENT);
        return at > 0 && Date.now() - at < CHECKOUT_INTENT_MAX_AGE;
      } catch { return false; }
    },
  };
  const ensureSynced = async () => { await whenIdle(); await sync(); await whenIdle(); };
  const syncedOrTimeout = () =>
    Promise.race([ensureSynced(), new Promise((resolve) => setTimeout(resolve, CHECKOUT_TIMEOUT))])
      .catch((error) => console.error('[p-cart-addons]', error));

  if (storage.take()) syncedOrTimeout().finally(() => window.location.assign(`${root}checkout`));

  let bypass = false;
  let checkingOut = false;
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const submitter = /** @type {SubmitEvent} */ (event).submitter;
    if (bypass || !(form instanceof HTMLFormElement) || submitter?.getAttribute('name') !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true;
    submitter.setAttribute('aria-busy', 'true');
    storage.set();
    syncedOrTimeout()
      .finally(() => {
        storage.take();
        submitter.removeAttribute('aria-busy');
        checkingOut = false;
        bypass = true;
        try {
          // Drawer/Seite können durch den Abgleich neu gerendert sein → Formular + Button frisch suchen
          const liveForm = form.isConnected ? form : document.getElementById(form.id);
          const liveButton = submitter.isConnected && submitter.form === liveForm
            ? submitter
            : liveForm?.id && document.querySelector(`[name="checkout"][form="${liveForm.id}"]`);
          if (liveForm instanceof HTMLFormElement && liveButton instanceof HTMLElement && liveButton.form === liveForm) {
            liveForm.requestSubmit(/** @type {HTMLButtonElement} */ (liveButton));
          } else {
            window.location.assign(`${root}checkout`);
          }
        } catch {
          window.location.assign(`${root}checkout`);
        } finally {
          bypass = false;
        }
      });
  }, true);
}
